import { CreateFacilityModal } from '@/components/CreateFacilityModal';
import { GenericSelect } from '@/components/GenericSelect';
import { useHasScopes } from '@/hooks/useHasScopes';
import { RestrictedComponent } from '@/layout/RestrictedComponent';
import { queryClient, trpc } from '@/trpcClient';
import { checkTransferMaterialInput, TransferMaterialInputType } from '@shelflix/schemas/material/transfer';
import { FacilityDto } from '@shelflix/trpc/routes/facility/list/useCase';
import Icon from '@shelflix/ui-web/components/Icon';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@shelflix/ui-web/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@shelflix/ui-web/components/ui/form';
import { Textarea } from '@shelflix/ui-web/components/ui/textarea';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowRightLeft, Building2, MapPin } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

interface TransferSectionProps {
  materialUuid: string;
  facility: {
    uuid: string;
    name: string;
    address: string;
    city: {
      uuid: string;
      name: string;
      ibgeCode: string;
    };
    state: {
      uuid: string;
      name: string;
      ibgeCode: string;
      stateName: string;
    };
  };
}

function normalizeSearch(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

export function TransferSection({ materialUuid, facility }: TransferSectionProps) {
  const transferMaterialMutation = useMutation(trpc.material.transfer.mutationOptions());
  const canCreateFacility = useHasScopes(['admin:facilities:create']);
  const [isCreateFacilityModalOpen, setIsCreateFacilityModalOpen] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState<FacilityDto | null>(null);

  const { data, isPending } = useQuery(
    trpc.facility.list.queryOptions({
      recordsPerPage: 9999,
      sort: { field: 'name', direction: 'asc' },
    })
  );

  const facilities = useMemo(() => {
    return (data?.facilities ?? [])
      .filter((item) => item.uuid !== facility.uuid)
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }));
  }, [data?.facilities, facility.uuid]);

  const form = useForm<TransferMaterialInputType>({
    defaultValues: {
      materialUuid: materialUuid,
      targetFacilityUuid: '',
      comments: '',
    },
    mode: 'all',
  });

  const onSubmit = (data: TransferMaterialInputType) => {
    if (transferMaterialMutation.isPending) return;
    transferMaterialMutation.mutate(data, {
      onSuccess: (result) => {
        toast.success(result.message);
        form.reset();
        window.location.reload();
      },
      onError: (error) => {
        toast.error(`Erro ao transferir material: ${error.message}`);
      },
    });
  };

  const selectCreatedFacility = (created: {
    uuid: string;
    name: string;
    cnpj: string;
    city: string;
    state: string;
    cityIbgeCode: string;
  }) => {
    setSelectedFacility({
      uuid: created.uuid,
      name: created.name,
      cnpj: created.cnpj,
      city: { uuid: '', name: created.city, ibgeCode: created.cityIbgeCode },
      state: { uuid: '', name: created.state, ibgeCode: '' },
    } as FacilityDto);
    form.setValue('targetFacilityUuid', created.uuid, { shouldValidate: true, shouldDirty: true });
  };

  return (
    <>
      <Card className="rounded-lg border border-gray-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg font-semibold text-[var(--primary)]">
            <ArrowRightLeft className="h-5 w-5" />
            Transferir Material
          </CardTitle>
          <p className="mb-3 text-sm">
            Transfira este material para outro estabelecimento.
            <br />
            Todas as campanhas e configurações serão mantidas.
          </p>
        </CardHeader>
        <CardContent>
          <div className="mb-6 rounded-lg bg-[var(--background)] p-4">
            <div className="flex items-start gap-3">
              <Building2 className="mt-0.5 h-5 w-5 text-gray-500" />
              <div>
                <h4 className="font-medium">Estabelecimento Atual</h4>
                <div className="mt-1 flex items-center gap-1">
                  <MapPin className="h-3 w-3 text-gray-400" />
                  <span className="text-xs text-gray-500">
                    {facility.name} - {facility.city.name} / {facility.state.name}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="targetFacilityUuid"
                rules={{ validate: (value) => checkTransferMaterialInput('targetFacilityUuid', value) }}
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel className="flex items-start gap-3 text-sm font-medium text-[var(--gray-primary)]">
                      <Building2 className="h-5 w-5 text-gray-500" />
                      <div className="flex flex-col items-start">
                        <span>Estabelecimento de Destino</span>
                      </div>
                    </FormLabel>
                    <FormControl>
                      <GenericSelect<FacilityDto>
                        value={selectedFacility}
                        onSelect={(item) => {
                          setSelectedFacility(item);
                          field.onChange(item.uuid);
                        }}
                        disabled={transferMaterialMutation.isPending || isPending}
                        loading={isPending}
                        dependencies={[facilities]}
                        listOnEmptySearch
                        placeholder={isPending ? 'Buscando estabelecimentos...' : 'Buscar estabelecimento de destino'}
                        fetchItems={async (search) => {
                          const term = normalizeSearch(search.trim());
                          if (!term) return facilities;

                          const digits = term.replace(/\D/g, '');
                          return facilities.filter((item) => {
                            const name = normalizeSearch(item.name);
                            const city = normalizeSearch(item.city?.name ?? '');
                            const state = normalizeSearch(item.state?.name ?? '');
                            const cnpj = item.cnpj?.replace(/\D/g, '') ?? '';
                            return (
                              name.includes(term) ||
                              city.includes(term) ||
                              state.includes(term) ||
                              (digits.length > 0 && cnpj.includes(digits))
                            );
                          });
                        }}
                        renderItem={(item) => (
                          <div className="flex flex-col items-start">
                            <span className="font-medium text-[var(--gray-primary)]">{item.name}</span>
                            <span className="text-xs">
                              {item.city?.name} / {item.state?.name}
                            </span>
                          </div>
                        )}
                        getItemLabel={(item) => item.name}
                        getItemKey={(item) => item.uuid}
                        footerAction={
                          canCreateFacility
                            ? {
                                label: 'Criar estabelecimento',
                                icon: <Icon name="add" size={18} />,
                                onClick: () => setIsCreateFacilityModalOpen(true),
                              }
                            : undefined
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="comments"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel className="text-sm font-medium text-[var(--gray-primary)]">
                      Comentários (opcional)
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Adicione comentários sobre a transferência..."
                        disabled={transferMaterialMutation.isPending}
                        className="min-h-[100px] resize-none"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="rounded-lg border border-amber-200 bg-[var(--background)] p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  <div>
                    <h4 className="font-medium">Atenção</h4>
                    <ul className="mt-1 space-y-1 text-sm">
                      <li>• Esta operação moverá o dispositivo para o novo estabelecimento</li>
                      <li>• Todas as campanhas agendadas serão mantidas</li>
                      <li>• O material será removido do estabelecimento atual</li>
                      <li>• Esta ação não pode ser desfeita facilmente</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <RestrictedComponent
                  requiredScopes={['material:transfer']}
                  behavior="disable"
                  showToast
                  toastMessage="Você não tem permissão para transferir materiais."
                >
                  <Button
                    type="submit"
                    loading={transferMaterialMutation.isPending}
                    disabled={!form.watch('targetFacilityUuid') || transferMaterialMutation.isPending}
                    className="bg-[var(--primary)] px-6 hover:bg-[var(--primary)]"
                  >
                    <ArrowRightLeft className="mr-2 h-4 w-4" />
                    {transferMaterialMutation.isPending ? 'Transferindo...' : 'Transferir Material'}
                  </Button>
                </RestrictedComponent>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
      <CreateFacilityModal
        open={isCreateFacilityModalOpen}
        onOpenChange={setIsCreateFacilityModalOpen}
        onFacilityCreated={async (created) => {
          await queryClient.invalidateQueries({ queryKey: trpc.facility.list.queryKey() });
          selectCreatedFacility(created);
        }}
      />
    </>
  );
}
